import React, { useState, useEffect, useRef, useCallback } from "react";
import "./SendPrivateMessageModal.css";
import showSwal from "./swal";
import ApiService from "../api/apiService";
import { common } from "../util/common";
import { useNavigate } from "react-router-dom";
import { buildConversationPath } from "../util/helperFunctions";

const SendPrivateMessageModal = ({
  closeModal,
  setPrivateMessageModalShow,
  privateMessageModalData,
  setPrivateMesageModalData,
}) => {
  const navigate = useNavigate();
  const [recipients, setRecipients] = useState([]);
  const [message, setMessage] = useState("");
  const [recipientQuery, setRecipientQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [activeSuggestionIndex, setActiveSuggestionIndex] = useState(-1);

  const [allUsers, setAllUsers] = useState([]);

  const [isSendingMessage, setSendingMessage] = useState(false);
  const [isLoadingUsers, setLoadingUsers] = useState(false);
  const receiverInput = useRef(null);

  const canSend = recipients.length > 0 && message.trim() !== "" && !isSendingMessage;

  const handleCloseModal = useCallback(() => {
    setPrivateMesageModalData(null);
    setPrivateMessageModalShow(false);
    if (typeof closeModal === "function") {
      closeModal();
    }
  }, [closeModal, setPrivateMesageModalData, setPrivateMessageModalShow]);

  useEffect(() => {
    if (privateMessageModalData) {
      setRecipients([{ id: privateMessageModalData?.id, name: privateMessageModalData?.display_name }]);
    }
  }, [privateMessageModalData]);

  useEffect(() => {
    const fetchUserNames = async () => {
      try {
        setLoadingUsers(true);
        const response = await ApiService.users.getAllusers();
        const { success, users } = response;
        if (success) {
          setAllUsers(users);
        } else {
          showSwal(common.errors.CONVERSATION_FETCH_USERNAME_ERROR);
        }
      } catch (error) {
        console.error("Error fetching user names:", error);
      } finally {
        setLoadingUsers(false);
      }
    };

    fetchUserNames();
  }, [handleCloseModal]);

  useEffect(() => {
    receiverInput.current?.focus();
  }, []);

  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === "Escape") {
        handleCloseModal();
      }
    };

    window.addEventListener("keydown", handleEscape);
    return () => window.removeEventListener("keydown", handleEscape);
  }, []);

  const handleAddRecipient = ({ id, name }) => {
    if (!recipients.some((recipient) => recipient.id === id)) {
      setRecipients((prevRecipients) => [...prevRecipients, { id, name }]);
    }
    setRecipientQuery("");
    setSuggestions([]);
    setActiveSuggestionIndex(-1);
    receiverInput.current?.focus();
  };

  const handleRemoveRecipient = (id) => {
    setRecipients((prevRecipients) => prevRecipients.filter((recipient) => recipient.id !== id));
  };

  const handleSearch = (query) => {
    setRecipientQuery(query);
    const normalizedQuery = query.trim().toLowerCase();

    if (normalizedQuery) {
      const selectedRecipientIds = recipients.map((recipient) => recipient.id);
      const filteredUsers = allUsers.filter(
        (user) =>
          user?.display_name?.toLowerCase().includes(normalizedQuery) &&
          !selectedRecipientIds.includes(user.id)
      );
      setSuggestions(filteredUsers);
      setActiveSuggestionIndex(filteredUsers.length > 0 ? 0 : -1);
    } else {
      setSuggestions([]);
      setActiveSuggestionIndex(-1);
    }
  };

  const handleRecipientInputKeyDown = (event) => {
    if (event.key === "ArrowDown" && suggestions.length > 0) {
      event.preventDefault();
      setActiveSuggestionIndex((prev) => (prev + 1) % suggestions.length);
      return;
    }

    if (event.key === "ArrowUp" && suggestions.length > 0) {
      event.preventDefault();
      setActiveSuggestionIndex((prev) => (prev <= 0 ? suggestions.length - 1 : prev - 1));
      return;
    }

    if (event.key === "Enter" && suggestions.length > 0) {
      event.preventDefault();
      const suggestionToAdd = suggestions[activeSuggestionIndex] || suggestions[0];
      if (suggestionToAdd) {
        handleAddRecipient({ id: suggestionToAdd.id, name: suggestionToAdd.display_name });
      }
      return;
    }

    if (event.key === "Backspace" && recipientQuery.trim() === "" && recipients.length > 0) {
      const lastRecipient = recipients[recipients.length - 1];
      handleRemoveRecipient(lastRecipient.id);
    }
  };

  const handleSend = async () => {
    if (!message || message.trim() === "" || recipients.length === 0) {
      return showSwal(common.errors.DIRECT_TO_MESSAGE_REQUIRED_FIELDS);
    }
    if (isSendingMessage) return;

    setSendingMessage(true);
    const recipientIds = recipients.map((recipient) => recipient.id);
    const request = {
      recipients: recipientIds,
      message: message.trim(),
    };

    const { success, conversation_id } = await ApiService.directMessage.sendDirectMessage(request);
    if (!success) {
      setSendingMessage(false);
      return showSwal(common.errors.DIRECT_TO_MESSAGE_ERROR);
    }

    setSendingMessage(false);
    handleCloseModal();
    showSwal(common.success.DIRECT_MESSAGE_SENT).then(() => {
      navigate(buildConversationPath(conversation_id));
    });
  };

  return (
    <div
      className='spm-modal-backdrop'
      onClick={(event) => {
        if (event.target.classList.contains("spm-modal-backdrop")) {
          handleCloseModal();
        }
      }}
    >
      <div className='spm-modal-container'>
        <button className='spm-close-btn' onClick={handleCloseModal} aria-label='Close compose message modal'>
          <i className='fa fa-times' aria-hidden='true'></i>
        </button>

        <div className='spm-modal-header'>
          <h2>New Private Message</h2>
          <p>Start a focused conversation with teammates.</p>
        </div>

        <div className='spm-to-section'>
          <label htmlFor='spm-recipient-input' className='spm-to-label'>
            Recipients
          </label>
          <div className='spm-recipients-container'>
            {recipients.map((recipient, index) => (
              <span key={`${index}-${recipient.id}`} className='spm-recipient-chip'>
                <span>{recipient.name}</span>
                <button
                  type='button'
                  className='spm-remove-recipient'
                  onClick={() => handleRemoveRecipient(recipient.id)}
                  aria-label={`Remove ${recipient.name}`}
                >
                  &times;
                </button>
              </span>
            ))}
            <input
              id='spm-recipient-input'
              type='text'
              className='spm-recipient-input'
              placeholder='Type a teammate name...'
              onChange={(e) => handleSearch(e.target.value)}
              onKeyDown={handleRecipientInputKeyDown}
              ref={receiverInput}
              value={recipientQuery}
            />
            {isLoadingUsers && <p className='spm-helper-text'>Loading users...</p>}
            {suggestions.length > 0 && (
              <ul className='spm-suggestions-list'>
                {suggestions.map((suggestion, index) => (
                  <li
                    key={`${index}-${suggestion.id}`}
                    className={`spm-suggestion-item ${activeSuggestionIndex === index ? "is-active" : ""}`}
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => handleAddRecipient({ id: suggestion.id, name: suggestion.display_name })}
                  >
                    <span>{suggestion.display_name}</span>
                    <small>Click or press Enter</small>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div className='spm-meta-row'>
            <span>{recipients.length} recipient{recipients.length !== 1 ? "s" : ""} selected</span>
            <span>Tip: use arrows + Enter to add quickly</span>
          </div>
        </div>

        <div className='spm-message-section'>
          <label htmlFor='spm-message-textarea' className='spm-message-label'>
            Message
          </label>
          <textarea
            id='spm-message-textarea'
            className='spm-message-textarea'
            rows='7'
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder='Type your message here. Press Enter for a new line.'
          />
          <div className='spm-meta-row'>
            <span>Keep it short and clear for faster replies.</span>
            <span>{message.length} chars</span>
          </div>
        </div>

        <div className='spm-footer'>
          <button type='button' className='spm-cancel-btn' onClick={handleCloseModal} disabled={isSendingMessage}>
            Cancel
          </button>
          <button type='button' className='spm-send-btn' onClick={handleSend} disabled={!canSend}>
            {isSendingMessage ? "Sending..." : "Send Message"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SendPrivateMessageModal;
